// The slim "no searches yet" branch of `shortlist-extension-alerts.tpl.html` — what the review
// screen shows a user whose alert list is empty.
//
// ⚠️ THREE MUTUALLY EXCLUSIVE BODIES, CHOSEN IN UPSTREAM'S ORDER, and the order is the point:
//   1. the department picker, when the whitelabel has departments and the user is in none;
//   2. the job picker, when automated hunters are on and there are jobs to start from;
//   3. otherwise "No active Searches" and ADD SEARCH.
// Only the slim branch is ported: the review state passes `force-slim="::true"`, so the full
// view's "It's time to create your first search!" body cannot render on this route.
//
// Class names are verbatim; `_shortlist-extension-alerts.scss` styles all three already and hangs
// them off `.shortlist-extension-alerts`, which `ExtensionAlertsHost` renders around this.
import { useRef, useState, type ReactElement, type ReactNode } from 'react'
import type { ExtensionJob, UserDepartment } from '@woo-ext/api'

export interface NoAlertsSlimProps {
  /**
   * `userDepartments`, already sorted "None of the above" last. Undefined until loaded — upstream
   * starts with `whitelabelHasDepartments = false`, so the picker cannot show before the answer.
   */
  departments?: readonly UserDepartment[]
  /** `featureFlags[AUTOMATED_HUNTERS]` (the candidates product only). */
  automatedHuntersEnabled?: boolean
  /** `extensionJobs` — the first page, searched on init with no query. */
  jobs?: readonly ExtensionJob[]
  /** `getExtensionJobTypeahead($viewValue)`, called for every keystroke and on focus. */
  onSearchJobs?: (query: string) => Promise<readonly ExtensionJob[]>
  /** `onExtensionJobSelected` — the job becomes the user's first alert. */
  onSelectJob?: (job: ExtensionJob) => void
  /** `createNewAlert()` — the edit screen on a new alert. */
  onCreateAlert?: () => void
  /** `updateExtensionUserDepartments([id])`. The picker gives way to the next body on success. */
  onSelectDepartment?: (departmentId: number) => Promise<void>
}

/** `ExtensionWhitelabelDepartmentApi.NoneOfTheAboveDepartmentId` — present for every whitelabel. */
const NONE_OF_THE_ABOVE = 1

export function NoAlertsSlim({
  departments,
  automatedHuntersEnabled = false,
  jobs = [],
  onSearchJobs,
  onSelectJob,
  onCreateAlert,
  onSelectDepartment,
}: NoAlertsSlimProps): ReactElement {
  // `isWhitelabelWithDepartments` — any department other than "None of the above".
  const whitelabelHasDepartments = (departments ?? []).some((d) => d.departmentId !== NONE_OF_THE_ABOVE)
  const [joined, setJoined] = useState(false)
  const userInDepartments = joined || (departments ?? []).some((d) => d.inDepartment)
  const picking = whitelabelHasDepartments && !userInDepartments

  return (
    <div className="no-alerts-container-all-views-slim">
      <div className={picking ? undefined : 'no-alerts-center-content'}>
        {picking ? (
          <DepartmentPicker
            departments={departments ?? []}
            onSelect={async (id) => {
              await onSelectDepartment?.(id)
              setJoined(true)
            }}
          />
        ) : automatedHuntersEnabled && jobs.length > 0 ? (
          <div className="no-alerts-container-slim no-alerts-container-slim-scrollable no-alerts-container-slim-with-jobs nicer-scroller mac-style">
            <NoAlertsIcon />
            <div className="no-alerts-text-container">
              Welcome to
              <br />
              Woo&apos;s Search
            </div>
            <div className="no-alerts-secondary-text-container">
              We&apos;ve created automated searches for your recent job postings.
              <br />
              Select below to view the matched candidates.
            </div>
            <JobPicker onSearch={onSearchJobs} onSelect={onSelectJob} />
          </div>
        ) : (
          <div className="no-alerts-container-slim">
            <NoAlertsIcon />
            <div className="no-alerts-text-container">No active Searches</div>
            <div className="no-alerts-button candidate-side">
              <button type="button" onClick={onCreateAlert}>
                <i className="fa fa-plus-circle new-alert-circle-icon" />
                <span className="new-alert-text">{'  Add Search'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function NoAlertsIcon(): ReactElement {
  return (
    <div className="fa-stack fa-5x no-alerts-slim-icons-container">
      <i className="fas fa-circle fa-stack-2x circle-icon-container" />
      <i className="fas fa-user fa-stack-1x user-icon-container" />
      <i className="far fa-search fa-stack-1x search-icon-container" />
    </div>
  )
}

/**
 * ⚠️ "None of the above" IS AN OPTION HERE, last. Upstream's `departmentsDisplay` maps every row of
 * the sorted list, unlike the preferences form, which filters it out — so a recruiter who belongs
 * to no listed department has something to choose.
 */
function DepartmentPicker({
  departments,
  onSelect,
}: {
  departments: readonly UserDepartment[]
  onSelect: (departmentId: number) => Promise<void>
}): ReactElement {
  const [selected, setSelected] = useState('')
  const [saving, setSaving] = useState(false)
  return (
    <div className="department-selection-container">
      <div className="department-selection-text">Select your department</div>
      <div className="department-selection-selector select">
        <select
          name="department"
          className="form-control input"
          value={selected}
          onChange={(event) => {
            setSelected(event.target.value)
          }}
        >
          <option value="" disabled>
            Select a department
          </option>
          {sortNoneOfTheAboveLast(departments).map((d) => (
            <option key={d.departmentId} value={String(d.departmentId)}>
              {d.departmentName}
            </option>
          ))}
        </select>
      </div>
      <div
        className={`select-department-button-container${selected === '' ? ' unselected-department-button-container' : ''}`}
      >
        {/* `click-and-disable`: inert while the save is in flight, and a no-op with nothing chosen. */}
        <button
          type="button"
          className="select-department-button"
          disabled={saving}
          onClick={() => {
            if (selected === '' || saving) return
            setSaving(true)
            void onSelect(Number(selected)).finally(() => {
              setSaving(false)
            })
          }}
        >
          Select
        </button>
      </div>
    </div>
  )
}

/** `sortNoneOfTheAboveDepartmentLast` — stable for every other row. */
export function sortNoneOfTheAboveLast(departments: readonly UserDepartment[]): UserDepartment[] {
  return [...departments].sort((a, b) => {
    if (a.departmentId !== NONE_OF_THE_ABOVE && b.departmentId !== NONE_OF_THE_ABOVE) return 0
    return a.departmentId === NONE_OF_THE_ABOVE ? 1 : -1
  })
}

/**
 * The `uib-typeahead` over `extension-job.typeahead.tpl.html`, with `typeahead-min-length="0"`:
 * focusing the empty field lists jobs. uib replaces the match element with the template, so the
 * stylesheet's `.dropdown-menu > li > .extension-job-typeahead` is a direct child, and the first
 * match is `active` until the pointer moves.
 */
function JobPicker({
  onSearch,
  onSelect,
}: {
  onSearch?: (query: string) => Promise<readonly ExtensionJob[]>
  onSelect?: (job: ExtensionJob) => void
}): ReactElement {
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<readonly ExtensionJob[]>([])
  const [active, setActive] = useState(0)
  const seq = useRef(0)
  const input = useRef<HTMLInputElement>(null)

  const search = (value: string): void => {
    if (onSearch === undefined) return
    // Keystrokes do not come back in order; only the latest request may write.
    const mine = (seq.current += 1)
    void onSearch(value).then(
      (results) => {
        if (mine !== seq.current) return
        setMatches(results)
        setActive(0)
      },
      () => undefined,
    )
  }

  const choose = (job: ExtensionJob): void => {
    setMatches([])
    setQuery(`${job.externalJobId} | ${job.title}`)
    onSelect?.(job)
  }

  const el = input.current
  return (
    <div className="extension-job-picker">
      <div className="extension-job-picker-input-wrap" style={{ position: 'relative' }}>
        <input
          ref={input}
          className="form-control extension-job-picker-input"
          name="extension-job-picker-input"
          type="text"
          placeholder="Select a job search"
          autoComplete="off"
          value={query}
          onFocus={() => {
            search(query)
          }}
          onBlur={() => {
            // After a click on a match has landed, which a blur would otherwise pre-empt.
            setTimeout(() => {
              setMatches([])
            }, 150)
          }}
          onChange={(event) => {
            setQuery(event.target.value)
            search(event.target.value)
          }}
          onKeyDown={(event) => {
            if (matches.length === 0) return
            if (event.key === 'ArrowDown') setActive((i) => (i + 1) % matches.length)
            else if (event.key === 'ArrowUp') setActive((i) => (i - 1 + matches.length) % matches.length)
            else if (event.key === 'Enter' || event.key === 'Tab') {
              const job = matches[active]
              if (job !== undefined) {
                event.preventDefault()
                choose(job)
              }
            } else if (event.key === 'Escape') setMatches([])
            else return
            if (event.key !== 'Tab') event.preventDefault()
          }}
        />
        {matches.length === 0 ? null : (
          <ul
            className="dropdown-menu"
            role="listbox"
            style={{
              display: 'block',
              top: `${String((el?.offsetTop ?? 0) + (el?.offsetHeight ?? 0))}px`,
              left: `${String(el?.offsetLeft ?? 0)}px`,
            }}
          >
            {matches.map((job, index) => (
              <li
                key={job.id}
                className={`uib-typeahead-match${index === active ? ' active' : ''}`}
                role="option"
                aria-selected={index === active}
                onMouseEnter={() => {
                  setActive(index)
                }}
                // `ng-click="selectMatch($index, $event)"` is on the li in uib's popup template.
                // The mousedown is swallowed so the input keeps focus and its blur does not close
                // the list before the click lands.
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
                onClick={() => {
                  choose(job)
                }}
              >
                <a className="extension-job-typeahead cursor-pointer" tabIndex={-1}>
                  <span className="title" title={job.title}>
                    {highlight(job.title, query)}
                  </span>
                  <span className="external-job-id">
                    <i className="external-job-id-icon fas fa-briefcase" />
                    <span>{highlight(job.externalJobId, query)}</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/** `uibTypeaheadHighlight` — every case-insensitive occurrence of the query, in `<strong>`. */
export function highlight(text: string, query: string): ReactNode {
  if (query === '') return text
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))
}
